import {
  Text,
  Alert,
  ActionIcon,
  Button,
  Group,
  Menu,
  Modal,
  Anchor,
  Mark,
  Textarea,
} from '@mantine/core';
import { IconCheck, IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import type { DealEvent } from '../../../../schemas/deal-event.schema';
import { useDealEventController } from './useDealEventController';
import { useState } from 'react';
import { EditTaskForm } from '@ayocore/tasks/client/components/EditTaskForm';
import { useTaskRepository, taskStatusRepository } from '@ayocore/tasks/client/rpc';
import logger from '@ayocore/platform/common/helpers/logger';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { CrmCreateTaskForm } from '@ayocore/erp/crm/client/components/tasks/CrmCreateTaskForm';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { useCommentRepository } from '@ayocore/comments/client/rpc';
import {
  EventsList,
  type ActiveTaskItem,
  type EventsFilterOption,
  type EventListItem,
} from '@ayocore/platform/client/components/Events';

interface DealEventsProps {
  deal: {
    id: number;
    uuid: string;
  };
}

function getCommentPreview(content?: string): string | null {
  if (!content) {
    return null;
  }

  return content.length > 120 ? `${content.substring(0, 120)}...` : content;
}

function getCommentUpdatedText(payload: Record<string, unknown> | null): string {
  const oldContent = payload?.old_comment_content as string | undefined;
  const newContent = payload?.new_comment_content as string | undefined;
  const oldPreview = getCommentPreview(oldContent);
  const newPreview = getCommentPreview(newContent);

  if (oldPreview && newPreview && oldPreview !== newPreview) {
    return `Комментарий изменен: "${oldPreview}" → "${newPreview}"`;
  }

  const content = payload?.comment_content as string | undefined;
  return getCommentPreview(content) || 'Комментарий изменен';
}

function getCommentDeletedText(payload: Record<string, unknown> | null): string {
  const deletedContent = payload?.deleted_comment_content as string | undefined;
  const content = payload?.comment_content as string | undefined;
  const preview = getCommentPreview(deletedContent || content);

  if (preview) {
    return `Комментарий удален: "${preview}"`;
  }

  return 'Комментарий удален';
}

function getEventText(event: DealEvent): string {
  const payload = event.payload as Record<string, unknown> | null;
  const taskTitle = (payload?.task_title as string) || undefined;
  const content = payload?.comment_content as string | undefined;
  const statusChanged = Boolean(payload?.status_changed);
  const assigneeChanged = Boolean(payload?.assignee_changed);
  const dueDateChanged = Boolean(payload?.due_date_changed);

  switch (event.event_type) {
    case 'status_change':
      return `Статус изменен: ${event.from_status?.name || 'Начало'} → ${event.to_status?.name || 'Неизвестно'}`;
    case 'task_created':
      return getTaskCreatedText(taskTitle, payload);
    case 'task_updated':
      return getTaskUpdatedText(taskTitle, payload, {
        statusChanged,
        assigneeChanged,
        dueDateChanged,
      });
    case 'task_completed':
      return 'Задача выполнена';
    case 'task_cancelled':
      return 'Задача отменена';
    case 'task_deleted':
      return 'Задача удалена';
    case 'comment_added':
      return getCommentPreview(content) || 'Комментарий добавлен';
    case 'comment_updated':
      return getCommentUpdatedText(payload);
    case 'comment_deleted':
      return getCommentDeletedText(payload);
    default:
      return 'Событие';
  }
}

function getTaskCreatedText(
  taskTitle: string | undefined,
  payload: Record<string, unknown> | null
): string {
  const title = taskTitle || 'Задача';
  const dueDate = payload?.due_date as string | undefined;
  const dueTime = payload?.due_time as string | undefined;

  if (dueDate || dueTime) {
    const dueParts: string[] = [];
    if (dueDate) dueParts.push(new Date(dueDate).toLocaleDateString('ru-RU'));
    if (dueTime) dueParts.push(dueTime.slice(0, 5));
    return `Задача создана: "${title}", срок ${dueParts.join(' ')}`;
  }
  return `Задача создана: "${title}"`;
}

function getTaskUpdatedText(
  taskTitle: string | undefined,
  payload: Record<string, unknown> | null,
  flags: { statusChanged: boolean; assigneeChanged: boolean; dueDateChanged: boolean }
): string {
  const title = taskTitle || 'Задача';
  const changes: string[] = [];
  if (flags.statusChanged) changes.push('статус');
  if (flags.assigneeChanged) changes.push('исполнитель');

  let base =
    changes.length > 0
      ? `Задача "${title}" изменена (${changes.join(', ')})`
      : `Задача "${title}" изменена`;

  if (flags.dueDateChanged) {
    const formatDue = (date?: string, time?: string): string => {
      const parts: string[] = [];
      if (date) parts.push(new Date(date).toLocaleDateString('ru-RU'));
      if (time) parts.push(time.slice(0, 5));
      return parts.join(' ') || '—';
    };
    base += `: ${formatDue(payload?.old_due_date as string, payload?.old_due_time as string)} → ${formatDue(payload?.new_due_date as string, payload?.new_due_time as string)}`;
  }
  return base;
}

function TaskEventText({ event, onOpenTask }: { event: DealEvent; onOpenTask: () => void }) {
  const payload = event.payload as Record<string, unknown> | null;
  const taskTitle = (payload?.task_title as string) || 'Задача';

  if (event.event_type === 'task_created') {
    const dueDate = payload?.due_date as string | undefined;
    const dueTime = payload?.due_time as string | undefined;
    let suffix = '';
    if (dueDate || dueTime) {
      const parts: string[] = [];
      if (dueDate) parts.push(new Date(dueDate).toLocaleDateString('ru-RU'));
      if (dueTime) parts.push(dueTime.slice(0, 5));
      suffix = `, срок ${parts.join(' ')}`;
    }
    return (
      <Text size='xs'>
        Создана задача "
        <Anchor component='button' type='button' size='xs' onClick={onOpenTask}>
          {taskTitle}
        </Anchor>
        "{suffix}
      </Text>
    );
  }

  if (event.event_type === 'task_updated') {
    const statusChanged = Boolean(payload?.status_changed);
    const assigneeChanged = Boolean(payload?.assignee_changed);
    const dueDateChanged = Boolean(payload?.due_date_changed);

    const oldStatusName = statusChanged ? (payload?.old_status_name as string) || '—' : null;
    const newStatusName = statusChanged ? (payload?.new_status_name as string) || '—' : null;

    const formatDue = (date?: string, time?: string): string => {
      const parts: string[] = [];
      if (date) parts.push(new Date(date).toLocaleDateString('ru-RU'));
      if (time) parts.push(time.slice(0, 5));
      return parts.join(' ') || '—';
    };

    const changeParts: Array<{ label: string; newValue: string }> = [];
    if (oldStatusName && newStatusName && oldStatusName !== newStatusName)
      changeParts.push({ label: oldStatusName, newValue: newStatusName });
    if (dueDateChanged)
      changeParts.push({
        label: formatDue(payload?.old_due_date as string, payload?.old_due_time as string),
        newValue: formatDue(payload?.new_due_date as string, payload?.new_due_time as string),
      });
    if (assigneeChanged)
      changeParts.push({
        label: (payload?.old_assignee_name as string) || '—',
        newValue: (payload?.new_assignee_name as string) || '—',
      });

    return (
      <Text size='xs'>
        Задача "
        <Anchor component='button' type='button' size='xs' onClick={onOpenTask}>
          {taskTitle}
        </Anchor>
        " изменена
        {changeParts.length > 0 && (
          <>
            :{' '}
            {changeParts.map((part, index) => (
              <span key={index}>
                {index > 0 && '; '}
                {part.label} →{' '}
                <Mark c='inherit' px={2}>
                  {part.newValue}
                </Mark>
              </span>
            ))}
          </>
        )}
      </Text>
    );
  }
  return <Text size='xs'>{getEventText(event)}</Text>;
}

export function DealEvents({ deal }: DealEventsProps) {
  const { events, comments, activeTasks, loading, error, reload } = useDealEventController(deal);
  const commentRepository = useCommentRepository();
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('asc');
  const [eventFilter, setEventFilter] = useState<'all' | 'comments'>('comments');
  const [showEditTask, setShowEditTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [showEditComment, setShowEditComment] = useState(false);
  // Инлайн-модалки (не через useModal): z-index из общего аллокатора, иначе при
  // открытии DealView в дравере модалки окажутся под ним.
  const editTaskModalZIndex = useSurfaceZIndex(showEditTask);
  const editCommentModalZIndex = useSurfaceZIndex(showEditComment);
  const [editingCommentUuid, setEditingCommentUuid] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const taskRepo = useTaskRepository();
  const createTaskModal = useModal(CrmCreateTaskForm);
  const currentUserId = useAuthController(s => s.user?.id);

  const handleDeleteTask = async (taskId: number) => {
    if (!confirm('Удалить эту задачу?')) return;
    try {
      await taskRepo.deleteById(taskId);
      reload();
    } catch (e) {
      logger.error('❌ Error deleting task:', e);
    }
  };

  const handleCompleteTask = async (taskId: number, taskTypeId: number) => {
    try {
      const statuses = await taskStatusRepository.findByTaskType(taskTypeId);
      const finishedStatus = statuses.find(s => s.slug === 'FINISHED');
      if (!finishedStatus) {
        logger.error('❌ [DealEvents] FINISHED status not found');
        return;
      }
      await taskRepo.update(taskId, { task_status_id: finishedStatus.id });
      reload();
    } catch (e) {
      logger.error('❌ [DealEvents] Error completing task:', e);
    }
  };

  const openEditComment = async (commentUuid: string) => {
    try {
      const comment = await commentRepository.findById(commentUuid);
      if (!comment) {
        logger.warn('[DealEvents] Comment not found for edit', { commentUuid });
        return;
      }

      setEditingCommentUuid(comment.uuid);
      setEditingCommentText(comment.content || '');
      setShowEditComment(true);
    } catch (e) {
      logger.error('❌ [DealEvents] Error loading comment for edit:', e);
    }
  };

  const closeEditComment = () => {
    setShowEditComment(false);
    setEditingCommentUuid(null);
    setEditingCommentText('');
  };

  const saveEditedComment = async () => {
    if (!editingCommentUuid || isSavingComment) {
      return;
    }

    const content = editingCommentText.trim();
    if (!content) {
      return;
    }

    setIsSavingComment(true);
    try {
      await commentRepository.update(editingCommentUuid, { content });
      closeEditComment();
      reload();
    } catch (e) {
      logger.error('❌ [DealEvents] Error updating comment:', e);
    } finally {
      setIsSavingComment(false);
    }
  };

  const deleteComment = async (commentUuid: string) => {
    if (!confirm('Удалить этот комментарий?')) {
      return;
    }

    try {
      await commentRepository.deleteById(commentUuid);
      reload();
    } catch (e) {
      logger.error('❌ [DealEvents] Error deleting comment:', e);
    }
  };

  if (error) return <Alert color='red'>{error}</Alert>;

  const filteredEvents = events.filter(event => {
    if (eventFilter === 'comments') return false;
    if (event.event_type === 'task_updated') {
      const p = event.payload as Record<string, unknown> | null;
      if (!p?.assignee_changed && !p?.due_date_changed) return false;
    }
    return true;
  });

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const aTime = new Date(a.created_at || 0).getTime();
    const bTime = new Date(b.created_at || 0).getTime();
    return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
  });
  const sortedComments = [...comments].sort((a, b) => {
    const aTime = new Date(a.created_at || 0).getTime();
    const bTime = new Date(b.created_at || 0).getTime();
    return sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
  });

  const filterOptions: EventsFilterOption[] = [
    { value: 'all', label: 'Все события' },
    { value: 'comments', label: 'Комментарии' },
  ];

  const activeTaskItems: ActiveTaskItem[] = activeTasks.map(task => ({
    id: task.id,
    title: task.title,
    dueLabel: task.due_date ? new Date(task.due_date).toLocaleDateString('ru-RU') : '—',
    assigneeLabel: task.assignee?.name || '—',
    onOpen: () => {
      setEditingTaskId(task.id);
      setShowEditTask(true);
    },
    actions: (
      <Menu withArrow position='bottom-end' withinPortal>
        <Menu.Target>
          <ActionIcon
            variant='subtle'
            size='xs'
            onClick={e => e.stopPropagation()}
            aria-label='Действия'
          >
            <IconDots size={14} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconEdit size={14} />}
            onClick={e => {
              e.stopPropagation();
              setEditingTaskId(task.id);
              setShowEditTask(true);
            }}
          >
            Редактировать
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCheck size={14} />}
            disabled={!task.task_type_id}
            onClick={e => {
              e.stopPropagation();
              if (task.task_type_id) {
                handleCompleteTask(task.id, task.task_type_id);
              }
            }}
          >
            Завершить
          </Menu.Item>
          <Menu.Item
            color='red'
            leftSection={<IconTrash size={14} />}
            onClick={e => {
              e.stopPropagation();
              handleDeleteTask(task.id);
            }}
          >
            Удалить
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    ),
  }));

  const eventItems: EventListItem[] = sortedEvents.map(event => {
    const meta = `${event.created_by?.name || '—'} • ${event.created_at ? new Date(event.created_at).toLocaleString('ru-RU') : ''}`;
    const isOwn = Boolean(currentUserId && event.created_by?.id === currentUserId);
    const isCommentEvent =
      event.event_type === 'comment_added' || event.event_type === 'comment_updated';
    const commentUuid = event.entity_uuid;
    const canManageComment = Boolean(isCommentEvent && commentUuid && isOwn);
    const commentActions =
      canManageComment && commentUuid ? (
        <Menu withArrow position='bottom-end' withinPortal>
          <Menu.Target>
            <ActionIcon
              variant='subtle'
              size='xs'
              onClick={e => e.stopPropagation()}
              aria-label='Действия комментария'
            >
              <IconDots size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={e => {
                e.stopPropagation();
                void openEditComment(commentUuid);
              }}
            >
              Редактировать
            </Menu.Item>
            <Menu.Item
              color='red'
              leftSection={<IconTrash size={14} />}
              onClick={e => {
                e.stopPropagation();
                void deleteComment(commentUuid);
              }}
            >
              Удалить
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : undefined;

    if (['task_created', 'task_updated'].includes(event.event_type)) {
      return {
        id: event.id,
        meta,
        isOwn,
        content: (
          <TaskEventText
            event={event}
            onOpenTask={() => {
              const task = activeTasks.find(t => t.uuid === event.entity_uuid);
              if (task) {
                setEditingTaskId(task.id);
                setShowEditTask(true);
              }
            }}
          />
        ),
      };
    }

    if (event.event_type === 'status_change') {
      return {
        id: event.id,
        meta,
        isOwn,
        content: (
          <Text size='xs'>
            Статус изменен: {event.from_status?.name || 'Начало'} →{' '}
            <Mark c='inherit' px={2}>
              {event.to_status?.name}
            </Mark>
          </Text>
        ),
      };
    }

    return {
      id: event.id,
      meta,
      isOwn,
      actions: commentActions,
      actionsVisibility: 'hover',
      content: <Text size='xs'>{getEventText(event)}</Text>,
    };
  });
  const commentItems: EventListItem[] = sortedComments.map(comment => {
    const authorName = comment.author?.name || comment.author?.email || '—';
    const ts = comment.created_at ? new Date(comment.created_at).toLocaleString('ru-RU') : '';
    const meta = [authorName, ts].filter(Boolean).join(' • ') || undefined;
    const isOwn = Boolean(currentUserId && comment.author_id === currentUserId);
    const commentActions = isOwn ? (
      <Menu withArrow position='bottom-end' withinPortal>
        <Menu.Target>
          <ActionIcon
            variant='subtle'
            size='xs'
            onClick={e => e.stopPropagation()}
            aria-label='Действия комментария'
          >
            <IconDots size={14} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconEdit size={14} />}
            onClick={e => {
              e.stopPropagation();
              void openEditComment(comment.uuid);
            }}
          >
            Редактировать
          </Menu.Item>
          <Menu.Item
            color='red'
            leftSection={<IconTrash size={14} />}
            onClick={e => {
              e.stopPropagation();
              void deleteComment(comment.uuid);
            }}
          >
            Удалить
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    ) : undefined;

    return {
      id: comment.uuid,
      meta,
      isOwn,
      actions: commentActions,
      actionsVisibility: 'hover',
      content: <Text size='xs'>{comment.content}</Text>,
    };
  });
  const visibleEvents = eventFilter === 'comments' ? commentItems : eventItems;

  return (
    <>
      <EventsList
        events={visibleEvents}
        loading={loading}
        tasks={activeTaskItems}
        filters={{
          options: filterOptions,
          active: eventFilter,
          onChange: value => setEventFilter(value as 'all' | 'comments'),
          sortOrder,
          onSortChange: setSortOrder,
        }}
        taskAction={{
          onClick: () => {
            createTaskModal.open(
              {
                entityUuid: deal.uuid,
                entityType: 'crm::DealRepository',
                title: 'Создать задачу',
                onCreated: () => {
                  createTaskModal.close();
                  reload();
                },
              },
              { inputSize: 'sm' }
            );
          },
        }}
        composer={{
          onSubmit: async content => {
            await commentRepository.create({
              entity_uuid: deal.uuid,
              entity_type: 'crm::DealRepository',
              content,
            });
          },
          onCreated: reload,
        }}
      />

      <Modal
        opened={showEditTask}
        onClose={() => {
          setShowEditTask(false);
          setEditingTaskId(null);
        }}
        title='Редактировать задачу'
        size='lg'
        zIndex={editTaskModalZIndex}
      >
        {editingTaskId != null && (
          <EditTaskForm
            taskId={editingTaskId}
            onTaskUpdated={() => {
              setShowEditTask(false);
              setEditingTaskId(null);
              reload();
            }}
          />
        )}
      </Modal>

      <Modal
        opened={showEditComment}
        onClose={closeEditComment}
        title='Редактировать комментарий'
        size='md'
        zIndex={editCommentModalZIndex}
      >
        <Textarea
          value={editingCommentText}
          onChange={e => setEditingCommentText(e.currentTarget.value)}
          minRows={4}
          autosize
          placeholder='Текст комментария'
        />
        <Group justify='flex-end' mt='md'>
          <Button variant='default' onClick={closeEditComment}>
            Отмена
          </Button>
          <Button
            onClick={() => void saveEditedComment()}
            loading={isSavingComment}
            disabled={!editingCommentText.trim()}
          >
            Сохранить
          </Button>
        </Group>
      </Modal>
    </>
  );
}
